import { useEffect, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { PORTAL_LOGIN_PATH } from '@/portal/config';
import { rootPathFor } from '@/portal/nextPath';
import { isSessionActive, usePortalSession } from '@/portal/stores/portalSessionStore';
import { roleFromToken } from '@/portal/utils/jwt';

interface ProtectedRouteProps {
  children: ReactNode;
  /** Which portal this route belongs to; a signed-in user of the other role is sent to their own portal. */
  role?: 'customer' | 'doctor';
}

const ProtectedRoute = ({ children, role = 'customer' }: ProtectedRouteProps) => {
  const token = usePortalSession((state) => state.token);
  const issuedAt = usePortalSession((state) => state.issuedAt);
  const endSession = usePortalSession((state) => state.endSession);
  const location = useLocation();

  const active = isSessionActive({ token, issuedAt });
  // Customer tokens that carry no account type predate the doctor portal, so they stay customers.
  const sessionRole = roleFromToken(token) ?? 'customer';

  useEffect(() => {
    if (token && !active) endSession();
  }, [token, active, endSession]);

  if (!active) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`${PORTAL_LOGIN_PATH}?next=${next}`} replace />;
  }

  if (sessionRole !== role) {
    return <Navigate to={rootPathFor(sessionRole)} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
